import React, { useState, useEffect, ImgHTMLAttributes } from 'react';

// This is here in order to ignore the browser's cache, since chrome's cache has problems with CORS,
// and the logos would always fallback to the default image.
const addTimestamp = (path: string) => `${path}?timestamp=${Date.now()}`;

const imgCache = {
  cache: {} as Record<string, any>,
  set(src: string, fallbackSrc: string, successCallback: () => void) {
    if (this.cache[src] || !src) {
      return;
    }

    this.cache[src] = 'loading';

    const img = new Image();
    img.crossOrigin = '*';
    const canvas: HTMLCanvasElement = document.createElement(
      'CANVAS'
    ) as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      if (ctx) {
        ctx.drawImage(img, 0, 0);
        this.cache[src] = canvas.toDataURL();
        successCallback();
      }
    };

    img.onerror = () => {
      if (fallbackSrc && !img.src.includes(fallbackSrc)) {
        img.src = addTimestamp(fallbackSrc);
      } else {
        img.style.display = 'none';
      }
    };

    img.src = src ? addTimestamp(src) : addTimestamp(fallbackSrc);
  }
};

interface IProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  fallbackSrc: string;
}

const CachedImg = React.forwardRef(
  ({ src, fallbackSrc, ...rest }: IProps, forwardedRef) => {
    const cachedSrc = imgCache.cache[src];
    const [cacheLoaded, setCacheLoaded] = useState(cachedSrc && cachedSrc !== 'loading');

    // If the same image is loaded twice at the same time, we need to handle the race condition
    // by setting an interval until the cached image is successfully loaded.
    useEffect(() => {
      let isMounted = true;
      let intervalId: NodeJS.Timeout;
      if (cachedSrc === 'loading') {
        intervalId = setInterval(() => {
          if (imgCache.cache[src] !== 'loading') {
            if (isMounted) {
              setCacheLoaded(true);
            }
            if (intervalId) {
              clearInterval(intervalId);
            }
          }
        }, 500);
      }

      imgCache.set(src, fallbackSrc, () => isMounted && setCacheLoaded(true));
      return () => {
        isMounted = false;
        if (intervalId) {
          clearInterval(intervalId);
        }
      };
    }, []);

    const imgProps = {
      ref: forwardedRef as React.MutableRefObject<HTMLImageElement>,
      ...rest
    };

    if (!cacheLoaded) {
      return <img src={fallbackSrc} {...imgProps} alt="" />;
    }

    const imgSrc = cacheLoaded ? cachedSrc : src;
    return <img src={imgSrc} {...imgProps} alt="" />;
  }
);

export default CachedImg;
